import {
    Component,
    Injector,
    OnDestroy,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { Guid, ScheduleStatus } from '@lsq-frontend/remote-services/enterprise/common';
import {
    InvoiceBatch,
    ListInvoiceBatchesRequest,
    ListInvoiceBatchesResponse,
    ListInvoiceBatchesService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    ClassNames,
    Color,
    DateFormat,
    DatePickerConfiguration,
    Modal
} from '@lsq-frontend/common-ui';
import { Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { ModalService, ModalWorkflowService } from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import { AdditionalUploadsModalComponent } from '../../customers/modals/additional-uploads-modal.component';
import { UploadsPreviewModalComponent } from '../../customers/modals/uploads-preview-modal.component';
import { DateRangeReportComponent } from '../../reports/base/date-range-report.component';
import { ReportsUserStateTypes } from '../../reports/reports.user-state-type';
import { IAdditionalUploadsModalConfig } from '../invoices.models';
import { UploadInvoicesService } from '../upload-invoices/upload-invoices.service';

@Component({
    selector: 'invoice-upload-history-report',
    styleUrls: ['./invoice-upload-history.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <div [ngClass]="{ 'min-page-width-large-up': !(dataSource?.loading$ | async) }">
            <lsq-banner
                [titleText]="title"
                [subTitle]="(dataSource.data$ | async)?.lastUpdatedDate | lastUpdatedDate"
                [hasContent]="true"
                [reportName]="title"
                [service]="apiService"
                [hideExport]="dataSource.noData$ | async"
                [analyticsName]="analyticsName"
                [datePickerConfiguration]="datePickerConfiguration"
            >
            </lsq-banner>
            <div class="container-fluid page-container">
                <div class="table">
                    <lsq-data-table-mobile
                        *ngIf="responsiveService.smBelow | async"
                        [dataSource]="dataSource"
                        [isPaginated]="true"
                    >
                        <ng-template #mobileCardHeader let-row="row">
                            <div class="mtc-header-content">
                                <div class="mtc-header-title">
                                    {{ row.uploadAmount | lsqCurrency }}
                                </div>
                                <div class="mtc-header-sub-title">
                                    <ng-container i18n="Used in: 'Uploaded at: {date}'">
                                        Uploaded at:
                                    </ng-container>
                                    {{ row.uploadDate | date: dateFormat.DateTime12Hour }}
                                </div>
                            </div>
                        </ng-template>

                        <ng-template #mobileCardColumns let-row="row">
                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ uploadNumberLabelI18n }}
                                </div>
                                <div class="card-item-value">
                                    <span *ngIf="!!row.batchNumber">{{ row.batchNumber }}</span>
                                    <span *ngIf="!row.batchNumber">
                                        {{ uploadPendingI18n }}
                                    </span>
                                </div>
                            </div>

                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ uploadAmountLabelI18n }}
                                </div>
                                <div class="card-item-value">
                                    {{ row.uploadAmount | lsqCurrency }}
                                </div>
                            </div>

                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ fileNameLabelI18n }}
                                </div>
                                <div class="card-item-value">
                                    <span *ngIf="row.fileNames.length === 1">{{
                                        row.fileNames[0]
                                    }}</span>
                                    <a
                                        *ngIf="row.fileNames.length > 1"
                                        class="preserve-case"
                                        href="javascript:void(0)"
                                        (click)="showFileNamesModal(row.fileNames)"
                                    >
                                        {{ multipleUploadsButtonI18n }}
                                    </a>
                                </div>
                            </div>

                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ additionalUploadsLabelI18n }}
                                    <lsq-icon-popover
                                        [text]="additionalUploadsDescription"
                                    ></lsq-icon-popover>
                                </div>
                                <div class="card-item-value">
                                    <a
                                        *ngIf="row.scheduleStatus < disabledStatus"
                                        href="javascript:void(0)"
                                        (click)="showAdditionalUploadsModal(row.invoiceBatchKey)"
                                        aria-label="Upload additional documents for this batch"
                                        i18n-aria-label
                                    >
                                        <lsq-icon [type]="icons.Upload"></lsq-icon>
                                    </a>
                                    <lsq-icon
                                        *ngIf="row.scheduleStatus >= disabledStatus"
                                        class="disabled"
                                        [type]="icons.Upload"
                                        [color]="color.Gray"
                                    >
                                    </lsq-icon>
                                </div>
                            </div>

                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ additionalFilesAddedLabelI18n }}
                                </div>
                                <div class="card-item-value">
                                    <span
                                        *ngIf="
                                            !!row.additionalFileNames &&
                                            row.additionalFileNames.length
                                        "
                                    >
                                        <lsq-icon [type]="icons.CheckmarkSmall"></lsq-icon>&nbsp;
                                        {{
                                            row.additionalFileNames.length
                                                | i18nPlural
                                                    : {
                                                          '=1': '1 Document Uploaded',
                                                          other: '# Documents Uploaded'
                                                      }
                                        }}
                                    </span>
                                </div>
                            </div>
                        </ng-template>
                    </lsq-data-table-mobile>

                    <lsq-data-table
                        *ngIf="!(responsiveService.smBelow | async)"
                        [dataSource]="dataSource"
                        [isPaginated]="true"
                        [hasFooter]="true"
                        [hasStickyHeader]="true"
                        (takeChanged)="handleTakeChange($event)"
                    >
                        <lsq-date-column
                            [isDateTimeMultiline]="true"
                            [column]="{
                                header: uploadDateLabelI18n,
                                key: 'uploadDate',
                                className: 'first-column'
                            }"
                        >
                            <ng-template [custom-footer-cell] let-row="row">
                                <div class="first-column">
                                    {{ uploadAmountTotalLabelI18n }}
                                </div>
                            </ng-template>
                        </lsq-date-column>

                        <lsq-column
                            [column]="{
                                header: uploadNumberLabelI18n,
                                key: 'batchNumber',
                                className: classNames.ColumnCurrency
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <span *ngIf="!!row.batchNumber">{{ row.batchNumber }}</span>
                                <span *ngIf="!row.batchNumber">
                                    {{ uploadPendingI18n }}
                                </span>
                            </ng-template>
                        </lsq-column>

                        <lsq-currency-column
                            [totalValue]="(dataSource.data$ | async)?.totalUploadAmount"
                            [column]="{
                                header: uploadAmountLabelI18n,
                                key: 'uploadAmount',
                                width: '12.5%'
                            }"
                        >
                        </lsq-currency-column>

                        <lsq-spacer-column width="5%"></lsq-spacer-column>

                        <lsq-column
                            [column]="{
                                header: fileNameLabelI18n,
                                key: 'fileNames',
                                className: 'file-name-column text-truncate',
                                sortable: false
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <span *ngIf="row.fileNames.length === 1">{{
                                    row.fileNames[0]
                                }}</span>
                                <a
                                    *ngIf="row.fileNames.length > 1"
                                    class="file-name-link"
                                    (click)="showFileNamesModal(row.fileNames)"
                                    >{{ multipleUploadsButtonI18n }}</a
                                >
                            </ng-template>
                        </lsq-column>

                        <lsq-column
                            [column]="{
                                header: additionalUploadsLabelI18n,
                                key: 'additionalUploads',
                                className: 'upload-button-cell',
                                headerPopoverContent: additionalUploadsDescription,
                                sortable: false,
                                width: '10rem'
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <a
                                    *ngIf="row.scheduleStatus < disabledStatus"
                                    href="javascript:void(0)"
                                    aria-label="Upload additional documents for this batch"
                                    i18n-aria-label
                                    (click)="showAdditionalUploadsModal(row.invoiceBatchKey)"
                                >
                                    <lsq-icon [type]="icons.Upload"></lsq-icon>
                                </a>
                                <lsq-icon
                                    *ngIf="row.scheduleStatus >= disabledStatus"
                                    class="disabled"
                                    [type]="icons.Upload"
                                    [color]="color.Gray"
                                >
                                </lsq-icon>
                            </ng-template>
                        </lsq-column>

                        <lsq-column
                            [column]="{
                                header: additionalFilesAddedLabelI18n,
                                key: 'additionalFilesAdded',
                                className: 'additional-files-added-cell',
                                sortable: false
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <span
                                    *ngIf="
                                        !!row.additionalFileNames && row.additionalFileNames.length
                                    "
                                >
                                    <lsq-icon [type]="icons.CheckmarkSmall"></lsq-icon>&nbsp;
                                    {{
                                        row.additionalFileNames.length
                                            | i18nPlural
                                                : {
                                                      '=1': '1 Document Uploaded',
                                                      other: '# Documents Uploaded'
                                                  }
                                    }}
                                </span>
                            </ng-template>
                        </lsq-column>
                    </lsq-data-table>
                </div>
            </div>
        </div>

        <ng-template #modalTemplate>
            <ul class="list-unstyled file-names-list">
                <li *ngFor="let file of fileNames">
                    <lsq-icon [type]="icons.CheckmarkSmall" [color]="color.Green"></lsq-icon>
                    {{ file }}
                </li>
            </ul>
        </ng-template>
    `
})
export class InvoiceUploadHistoryComponent extends DateRangeReportComponent implements OnDestroy {
    @ViewChild('modalTemplate', { read: TemplateRef })
    public modalTemplate: TemplateRef<any>;
    public title = 'Upload History';

    public analyticsName = AnalyticsValue.InvoiceUploadHistory;
    public userStateType = ReportsUserStateTypes.ListInvoiceBatchesRequest;
    public savedDateFilterName = null;

    // Constants
    public icons = Icons;
    public color = Color;
    public dateFormat = DateFormat;
    public classNames = ClassNames;
    public disabledStatus = ScheduleStatus.AeApproval;

    public additionalUploadsDescription = this._i18n({
        description: `Describes how to use the 'additional uploads' button for an invoice batch`,
        value: `You can upload missing documents to your invoice submission here. If the submission is too far into the process you will be unable to make additions.`
    });
    public uploadDateLabelI18n = this._i18n({
        meaning: `Label for 'date uploaded'`,
        value: `Date`
    });
    public uploadNumberLabelI18n = this._i18n({
        meaning: `Label for 'upload number'`,
        value: `Upload #`
    });
    public uploadAmountLabelI18n = this._i18n({
        meaning: `Label for 'upload amount', the dollar amount of invoices uploaded in this batch`,
        value: 'Upload Amount'
    });
    public fileNameLabelI18n = this._i18n({
        meaning: `Label for 'file name'`,
        value: 'File Name'
    });
    public additionalUploadsLabelI18n = this._i18n({
        meaning: `Label for 'additional uploads' button`,
        value: 'Additional Uploads'
    });
    public additionalFilesAddedLabelI18n = this._i18n({
        meaning: `Label for 'number of additional files added'`,
        value: 'Additional Files Added'
    });
    public multipleUploadsButtonI18n = this._i18n({
        meaning:
            `Multiple files were uploaded to create this batch. Click to view the ` +
            `files associated with this upload.`,
        value: 'Multiple'
    });
    public uploadPendingI18n = this._i18n({
        meaning: `Because the invoice batch is pending, it does not yet have an upload number`,
        value: 'Pending'
    });
    public uploadAmountTotalLabelI18n = this._i18n({
        meaning: `Label for grand total of 'upload amount' column`,
        value: 'Total'
    });
    private _additionalUploadsTitleI18n = this._i18n({
        meaning: `Title for 'additional uploads' dialog, used to upload documents`,
        value: 'Additional Uploads'
    });
    private _previouslyUploadedDocumentsTitleI18n = this._i18n({
        meaning: `Title for 'previously uploaded documents' dialog`,
        value: 'Previously Uploaded Documents'
    });

    public fileNames: string[];
    public datePickerConfiguration = new DatePickerConfiguration();
    public hideExport = false;
    public dataSource = new LsqApiServiceListSource<
        ListInvoiceBatchesResponse,
        ListInvoiceBatchesRequest,
        InvoiceBatch
    >(this.apiService);

    constructor(
        private _i18n: I18n,
        private _uploadInvoicesService: UploadInvoicesService,
        private _modalService: ModalService,
        private _workflowService: ModalWorkflowService,
        public apiService: ListInvoiceBatchesService,
        public responsiveService: ResponsiveService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();
    }

    public ngOnDestroy(): void {
        this._uploadInvoicesService.invoiceBatchAdditionalUploadsMap.clear();
    }

    public showFileNamesModal(names: string[]): void {
        this.fileNames = names;
        this._modalService.showModal(
            new Modal({
                id: 'invoice-upload-history-files-names',
                title: this._previouslyUploadedDocumentsTitleI18n,
                className: 'view-filenames',
                templateRef: this.modalTemplate,
                showFooter: false,
                showCloseButton: true
            })
        );
    }

    public showAdditionalUploadsModal(masterInvoiceBatchKey: Guid): void {
        this._workflowService.showWorkflow(
            [
                new Modal({
                    id: 'additional-uploads-modal',
                    title: this._additionalUploadsTitleI18n,
                    component: AdditionalUploadsModalComponent,
                    width: '70rem'
                }),
                new Modal({
                    id: 'uploads-preview-modal',
                    title: this._additionalUploadsTitleI18n,
                    component: UploadsPreviewModalComponent,
                    width: 'auto',
                    className: 'uploads-preview-component',
                    clickOutsideToClose: false
                })
            ],
            0,
            {
                masterInvoiceBatchKey,
                onSuccess: () => this.dataSource.dispatchRequest({}),
                mobileUpload: false
            } as IAdditionalUploadsModalConfig
        );
    }
}
